import React from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Typography, Menu, type MenuProps, theme } from 'antd';
import { isEnterprise, isCustom } from '@prerender/billing-shared';

import AdminTemplate from '../../layout/AdminTemplate';
import { useEvent } from '../../features/events/hooks/useEvent';
import { usePrerenderUser } from '../../hooks/usePrerenderUser';
import BILLING_PAGES from './availablePages';

type MenuItem = Required<MenuProps>['items'][number];

const META_TAGS = {
  title: 'Billing - Prerender.io',
};

function BillingLayout() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = usePrerenderUser();
  const { track } = useEvent();

  const isEnterpriseUser = isEnterprise(user.chargebeePlanId);
  // TODO: remove "customPrice" check once user have migrated to new 2025 pricing plans
  const isCustomPlanUser = typeof user.customPrice === 'number' || isCustom(user.chargebeePlanId);

  const items: (MenuItem & { hidden?: boolean })[] = React.useMemo(
    () =>
      [
        { label: 'Overview', key: BILLING_PAGES.overview },
        { label: 'Subscription Plans', key: BILLING_PAGES.plans, hidden: isCustomPlanUser },
        { label: 'Billing History', key: BILLING_PAGES.history },
        { label: 'Billing Info', key: BILLING_PAGES.payment },
        { label: 'Refer & Earn', key: BILLING_PAGES.referral, hidden: isEnterpriseUser },
      ].filter((item) => !item.hidden),
    [isCustomPlanUser, isEnterpriseUser]
  );

  const activeTab = React.useMemo(() => {
    const activePath = pathname.split('/billing/').pop();
    if (activePath?.includes(BILLING_PAGES.plans)) return BILLING_PAGES.plans;

    return activePath || BILLING_PAGES.overview;
  }, [pathname]);

  const onTabChange: MenuProps['onClick'] = (event) => {
    const params = { subscription_plan: user.planName, location: 'Navigation' };
    switch (event.key) {
      case BILLING_PAGES.overview:
        track('Billing Overview Page Viewed', params);
        break;
      case BILLING_PAGES.plans:
        track('Billing Plans Page Viewed', params);
        break;
      case BILLING_PAGES.history:
        track('Billing History Viewed', params);
        break;
      case BILLING_PAGES.payment:
        track('Payment Settings Viewed', params);
        break;
      case BILLING_PAGES.referral:
        track('Referral Page Viewed', params);
        break;
      default:
        break;
    }
    // Absolute: relative targets resolve against the full splat match in react-router 7.
    navigate(`/billing/${event.keyPath[0]}`);
  };

  return (
    <AdminTemplate metaTags={META_TAGS}>
      <div style={{ marginBottom: token.marginXL }}>
        <Typography.Title level={2}>Billing</Typography.Title>
        <Menu
          style={{ backgroundColor: 'transparent' }}
          onClick={onTabChange}
          selectedKeys={[activeTab]}
          mode="horizontal"
          items={items}
        />
      </div>

      <Outlet />
    </AdminTemplate>
  );
}

export default BillingLayout;
